import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { createFeature, getPage, listPageFeatures } from "../api/reports";
import { ApiError } from "../api/client";
import type { TenantPage, TenantPageFeature } from "../types";
import { Modal } from "../components/primitives";
import { PageHeader } from "../components/Chrome";
import { toast } from "../components/Toast";

// A5 — Page detail. Shows the features on a page; per-person feature
// overrides are managed from the Person detail screen.
export function AdminPageDetail({ tenantSlug }: { tenantSlug: string }) {
  const { pageId } = useParams<{ pageId: string }>();
  const id = Number(pageId);
  const [page, setPage] = useState<TenantPage | null>(null);
  const [features, setFeatures] = useState<TenantPageFeature[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const reload = async () => {
    try {
      const [p, f] = await Promise.all([
        getPage(tenantSlug, id),
        listPageFeatures(tenantSlug, id),
      ]);
      setPage(p);
      setFeatures(f);
    } catch (e) {
      setError(e instanceof ApiError ? e.detail : (e as Error).message);
    }
  };

  useEffect(() => {
    if (!Number.isFinite(id)) return;
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug, id]);

  if (error) return <div className="container"><div className="empty err">{error}</div></div>;
  if (!page) return <div className="container"><div className="empty">Loading…</div></div>;

  return (
    <div className="container">
      <PageHeader title={page.name}>
        <Link to="/admin/pages" className="btn-link">← Pages</Link>
        <button className="btn-primary" onClick={() => setCreateOpen(true)}>
          + Add feature
        </button>
      </PageHeader>
      {page.description && (
        <p style={{ color: "var(--ink-3)" }}>{page.description}</p>
      )}
      <div className="meta-grid">
        <div>
          <span className="meta-label">Slug</span>
          <code>{page.slug}</code>
        </div>
        <div>
          <span className="meta-label">Metabase dashboard</span>
          <span>{page.metabase_dashboard_id ?? "—"}</span>
        </div>
        <div>
          <span className="meta-label">Metabase collection</span>
          <span>{page.metabase_collection_id ?? "—"}</span>
        </div>
      </div>

      <h2 className="section-title">Features</h2>
      {features === null && <div className="empty">Loading…</div>}
      {features && features.length === 0 && (
        <div className="empty">
          No features yet. Click "+ Add feature" to map a Metabase card to this page.
        </div>
      )}
      {features && features.length > 0 && (
        <ul className="feature-list-static">
          {features.map((f) => (
            <li key={f.id}>
              <div className="feature-name">{f.name}</div>
              {f.description && <div className="feature-desc">{f.description}</div>}
              <div className="page-card-meta">slug: <code>{f.slug}</code>{f.metabase_card_id != null ? ` · card ${f.metabase_card_id}` : ""}</div>
            </li>
          ))}
        </ul>
      )}
      {createOpen && (
        <CreateFeatureModal
          tenantSlug={tenantSlug}
          pageId={id}
          onClose={() => setCreateOpen(false)}
          onCreated={async () => {
            setCreateOpen(false);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function CreateFeatureModal({
  tenantSlug,
  pageId,
  onClose,
  onCreated,
}: {
  tenantSlug: string;
  pageId: number;
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [cardId, setCardId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setErr(null);
    try {
      await createFeature(tenantSlug, pageId, {
        slug,
        name,
        description: description || null,
        metabase_card_id: cardId ? Number(cardId) : null,
      });
      toast(`Added feature ${name}`);
      await onCreated();
    } catch (e) {
      setErr(e instanceof ApiError ? e.detail : (e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal onClose={onClose} width={480}>
      <h2>New feature</h2>
      <p style={{ color: "var(--ink-3)" }}>
        Each feature wraps one Metabase card on the dashboard.
      </p>
      <label className="field-label">Slug</label>
      <input
        type="text"
        placeholder="e.g. contacts-directory"
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
        className="text-input"
        autoFocus
      />
      <label className="field-label">Display name</label>
      <input
        type="text"
        placeholder="e.g. Contacts directory"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="text-input"
      />
      <label className="field-label">Description (optional)</label>
      <input
        type="text"
        placeholder="What this feature shows"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="text-input"
      />
      <label className="field-label">Metabase card ID (optional)</label>
      <input
        type="number"
        placeholder="e.g. 73"
        value={cardId}
        onChange={(e) => setCardId(e.target.value)}
        className="text-input"
      />
      {err && <div className="form-err">{err}</div>}
      <div className="modal-actions">
        <button className="btn-secondary" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn-primary"
          onClick={submit}
          disabled={!slug || !name || submitting}
        >
          {submitting ? "Adding…" : "Add feature"}
        </button>
      </div>
    </Modal>
  );
}
